import { useStores } from "@stores";
import { formatPercent } from "@utils/Format";
import { observer } from "mobx-react-lite";

import { Icon } from "@components/Icon";

import { ChangeCell, FundingRateText, MonoText } from "./styles";
import { formatCompact } from "./utils";

interface MarketRowDynamicCellsProps {
  marketAddr: string;
  baseSymbol: string;
}

// Observer - only this part re-renders when ticker data for this market updates
export const MarketRowDynamicCells = observer(({ marketAddr, baseSymbol }: MarketRowDynamicCellsProps) => {
  const { marketStore } = useStores();

  const market = marketStore.allMarkets.find((m) => m.addr === marketAddr);
  const marketData = marketStore.getMarketData(marketAddr);

  if (!market) return null;

  const change24hValue = marketData.change24h;
  const change24h = formatPercent(change24hValue);
  const type = change24hValue > 0 ? "bid" : change24hValue < 0 ? "ask" : "neutral";

  const fundingRateValue = Number(market.funding_rate_percentage) || 0;
  const isFundingPositive = fundingRateValue >= 0;
  const fundingRate = `${isFundingPositive ? "+" : ""}${fundingRateValue.toFixed(6)}%`;
  const volume24h = `$${formatCompact(market.turnover_24h)}`;
  const openInterest = `$${formatCompact(market.open_interest_value)}`;

  return (
    <>
      <MonoText data-testid={`market-select:row:${baseSymbol}:last-price`}>${market.last_price || "-"}</MonoText>
      <ChangeCell data-testid={`market-select:row:${baseSymbol}:change-24h`} type={type}>
        <Icon
          color={type === "bid" ? "green-strong" : type === "ask" ? "red-strong" : "gray-11"}
          icon={
            type === "bid" ? "ri-arrow-right-up-line" : type === "ask" ? "ri-arrow-right-down-line" : "ri-subtract-line"
          }
          size={14}
        />
        <span>{change24h}</span>
      </ChangeCell>
      <FundingRateText
        data-testid={`market-select:row:${baseSymbol}:funding`}
        direction={isFundingPositive ? "positive" : "negative"}
      >
        {fundingRate}
      </FundingRateText>
      <MonoText data-testid={`market-select:row:${baseSymbol}:volume-24h`}>{volume24h}</MonoText>
      <MonoText data-testid={`market-select:row:${baseSymbol}:open-interest`} align="right">
        {openInterest}
      </MonoText>
    </>
  );
});
